import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, type ListPayload } from "./api";
import { useApi } from "./use-api";
import { keys } from "./query-keys";

/**
 * Types the `meta` bag every mutation carries, so the global error handler in
 * main.tsx and the mutations that opt out of it agree on one spelling. Without
 * this augmentation `meta` is Record<string, unknown> and a typo in either place
 * type-checks and silently does nothing.
 */
declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      /** Handle this mutation's error in the UI; keep the global toast away. */
      silenceErrorToast?: boolean;
    };
  }
}

/** GET /admin/me. Mirrors models.StaffUserResponse. */
export interface StaffUser {
  id: string;
  clerk_user_id: string;
  email: string;
  name?: string;
  is_staff: boolean;
  created_at: string;
  last_seen_at?: string;
}

/**
 * The identity call the whole shell hangs off.
 *
 * `retry: false` is deliberate: the answers that matter here are 401 and 403,
 * and neither becomes truer on a second attempt. Retrying a non-staff user's
 * request three times just delays the access screen.
 */
export function useMe() {
  const api = useApi();
  return useQuery({
    queryKey: keys.me,
    queryFn: () => api.get<StaffUser>("/me"),
    staleTime: 60_000,
    retry: false,
  });
}

/*
 * Response shapes. Each one mirrors a Go struct field for field -- the names are
 * the JSON tags, not renamed to camelCase, so a field can be traced from a
 * column to a cell without a translation table in between.
 */

/** One row of GET /admin/transactions. See admin.TransactionListItem. */
export interface TransactionListItem {
  order_id: string;
  storefront_id: string;
  /** Null when the LEFT JOIN found no storefront. Render the em dash, not the id. */
  storefront_name: string | null;
  /** A decimal string, never a number. Format it with formatIDR. */
  amount: string;
  payment_status: string;
  payment_method: string;
  payment_provider: string;
  source: string;
  /** Absent for v1-created and pre-migration-010 rows, which is most of them. */
  expires_at?: string;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
}

/**
 * One line item. See admin.TransactionItemView.
 *
 * `amount` is a QUANTITY -- it shares a name with transactions.amount and is not
 * money. unit_price and total are the money, and are decimal strings.
 */
export interface TransactionItemView {
  product_name: string;
  variant_name: string;
  amount: number;
  unit_price: string;
  total: string;
  logo?: string;
}

/** The safe storefront view. See models.StorefrontResponse. */
export interface StorefrontResponse {
  storefront_id: string;
  storefront_name: string;
  shop_domain?: string;
  shopify_api_version?: string;
  has_shopify_credentials: boolean;
  /** "oauth", "static", or absent when there are none. */
  credential_type?: string;
  callback_url?: string;
  callback_enabled: boolean;
  has_callback_secret: boolean;
  /** The tail of the signing secret, never the secret. */
  callback_secret_hint?: string;
  callback_secret_rotated_at?: string;
  created_at?: string;
  updated_at?: string;
}

/** GET /admin/transactions/:order_id. See admin.TransactionDetail. */
export interface TransactionDetail {
  order_id: string;
  storefront_id: string;
  storefront?: StorefrontResponse;
  amount: string;
  payment_provider: string;
  payment_method: string;
  payment_status: string;
  source: string;
  /** Not money: the schema gave every money column a minor-unit shadow and
   *  deliberately skipped this one. */
  extra_rate: number;
  additional_info: Record<string, unknown> | null;
  expires_at?: string;
  settled_amount?: string;
  settlement_fee?: string;
  transaction_items: TransactionItemView[];
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
  /** The Shopify order behind this payment, when it came through the bridge.
   *  Absent for a direct API order, which is most of them. */
  bridge_order_id?: string;
}

/** One row of GET /admin/storefronts. See admin.StorefrontListItem. */
export interface StorefrontListItem extends StorefrontResponse {
  transaction_count: number;
}

/**
 * The filters GET /admin/transactions accepts. Every one is optional.
 *
 * A type alias rather than an interface, here and below: only an alias gets
 * TypeScript's implicit index signature, and both filter types are handed to
 * queryString() and to the query-key factory, which take a plain record.
 */
export type TransactionFilters = {
  storefront_id?: string;
  payment_status?: string;
  payment_method?: string;
  payment_provider?: string;
  /** A prefix match on the server, not exact. */
  order_id?: string;
  /** An exact match on the server. */
  source?: string;
  /** RFC 3339 or a bare YYYY-MM-DD; a bare end_date means end of that day. */
  start_date?: string;
  end_date?: string;
  /** Absent means live rows only -- the server does not treat nil as "either". */
  is_deleted?: boolean;
  limit?: number;
  offset?: number;
}

export type StorefrontFilters = {
  /** Matches storefront_name or shop_domain. */
  search?: string;
  limit?: number;
  offset?: number;
}

/**
 * Serialises filters onto a query string, dropping anything unset.
 *
 * Empty strings are dropped rather than sent: `?source=` is a filter the server
 * would try to apply, and an operator who cleared a box means "no filter", not
 * "match the empty string".
 */
function queryString(filters: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  const encoded = params.toString();
  return encoded ? `?${encoded}` : "";
}

/**
 * Retry transport failures, never the server's considered answer.
 *
 * A 400 from a bad filter, a 404 for an order that does not exist and a 409 on a
 * duplicate domain are all final. Retrying them delays the screen that explains
 * what happened and doubles the log noise while it does.
 */
function retryTransportOnly(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status < 500) return false;
  return failureCount < 1;
}

/**
 * GET /admin/transactions.
 *
 * keepPreviousData holds the current page on screen while the next one loads.
 * Without it, paging or changing a filter blanks the table for a round trip and
 * the whole screen jumps.
 */
export function useTransactions(filters: TransactionFilters) {
  const api = useApi();
  return useQuery({
    queryKey: keys.transactions.list(filters),
    queryFn: () => api.get<ListPayload<TransactionListItem>>(`/transactions${queryString(filters)}`),
    placeholderData: keepPreviousData,
    retry: retryTransportOnly,
  });
}

/** GET /admin/transactions/:order_id. Opens soft-deleted orders too. */
export function useTransaction(orderId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.transactions.detail(orderId),
    queryFn: () => api.get<TransactionDetail>(`/transactions/${encodeURIComponent(orderId)}`),
    retry: retryTransportOnly,
  });
}

/** GET /admin/storefronts. */
export function useStorefronts(filters: StorefrontFilters = {}) {
  const api = useApi();
  return useQuery({
    queryKey: keys.storefronts.list(filters),
    queryFn: () => api.get<ListPayload<StorefrontListItem>>(`/storefronts${queryString(filters)}`),
    placeholderData: keepPreviousData,
    retry: retryTransportOnly,
  });
}

/** GET /admin/storefronts/:id. */
export function useStorefront(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.storefronts.detail(id),
    queryFn: () => api.get<StorefrontResponse>(`/storefronts/${encodeURIComponent(id)}`),
    retry: retryTransportOnly,
  });
}

export interface CreateStorefrontInput {
  storefront_name: string;
  shop_domain?: string;
}

/**
 * POST /admin/storefronts. A tier-1 write: it creates and destroys nothing, so
 * there is no typed confirmation. The audit row is written server-side inside
 * the same transaction as the insert.
 *
 * silenceErrorToast keeps the global handler in main.tsx off this one. A 409 is
 * the realistic failure -- re-adding a merchant that already exists -- and it is
 * answered next to the shop_domain field, naming the storefront that has it. A
 * toast on top of that would say less, louder.
 */
export function useCreateStorefront() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    meta: { silenceErrorToast: true },
    mutationFn: (input: CreateStorefrontInput) =>
      api.post<StorefrontResponse>("/storefronts", input),
    onSuccess: (created) => {
      // Prefix match: every filtered variant of the list is now stale.
      void queryClient.invalidateQueries({ queryKey: keys.storefronts.all });
      queryClient.setQueryData(keys.storefronts.detail(created.storefront_id), created);
    },
  });
}

/*
 * Merchants and their pricing (PRO-993/994, screens PRO-1200). The legal
 * counterparty above storefronts: fees, balance, invoices and payouts all hang
 * off a merchant, not a shop.
 */

/** GET /admin/merchants item. See models.MerchantResponse. */
export interface MerchantResponse {
  merchant_id: string;
  name: string;
  status: "active" | "suspended";
  npwp?: string;
  address?: string;
  bank_code?: string;
  bank_account_holder?: string;
  /** Never the account number: the last four digits, for recognition. */
  bank_account_last4?: string;
  has_bank_account: boolean;
  storefront_count: number;
  created_at: string;
  updated_at: string;
}

/** GET /admin/merchants/:id. See admin.MerchantDetail. */
export interface MerchantDetail extends MerchantResponse {
  storefronts: (StorefrontResponse & { merchant_id: string })[];
}

export type MerchantFilters = {
  search?: string;
  status?: string;
  limit?: number;
  offset?: number;
};

/** GET /admin/merchants. */
export function useMerchants(filters: MerchantFilters = {}) {
  const api = useApi();
  return useQuery({
    queryKey: keys.merchants.list(filters),
    queryFn: () => api.get<ListPayload<MerchantResponse>>(`/merchants${queryString(filters)}`),
    placeholderData: keepPreviousData,
    retry: retryTransportOnly,
  });
}

/** GET /admin/merchants/:id. */
export function useMerchant(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.merchants.detail(id),
    queryFn: () => api.get<MerchantDetail>(`/merchants/${encodeURIComponent(id)}`),
    retry: retryTransportOnly,
  });
}

/** The payment_method enum, as the fee schedule endpoints accept it. */
export const PAYMENT_METHODS = ["QRIS", "VA", "Bank Transfer", "Debit / Credit Card"] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/** One pricing window. See models.FeeScheduleResponse. Money is a decimal
 *  string in rupiah; rates are basis points (70 = 0.70%). */
export interface FeeSchedule {
  fee_schedule_id: string;
  merchant_id: string;
  payment_method: PaymentMethod;
  effective_from: string;
  /** Exclusive. Absent means open-ended. */
  effective_to?: string;
  in_force: boolean;
  percentage_bps: number;
  fixed: string;
  cost_percentage_bps: number;
  cost_fixed: string;
  below_cost: boolean;
  below_cost_override: boolean;
  below_cost_reason?: string;
  created_by_email?: string;
  created_at: string;
  closed_at?: string;
}

/** GET /admin/merchants/:id/fee-schedules. The whole history, unpaginated. */
export function useFeeSchedules(merchantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.feeSchedules.detail(merchantId),
    queryFn: () =>
      api.get<ListPayload<FeeSchedule>>(`/merchants/${encodeURIComponent(merchantId)}/fee-schedules`),
    select: (payload) => payload.items,
    retry: retryTransportOnly,
  });
}

/** The rate fields shared by a single create and a backfill row. */
export interface FeeRateInput {
  payment_method: PaymentMethod;
  percentage_bps: number;
  fixed?: string;
  cost_percentage_bps: number;
  cost_fixed?: string;
  below_cost_override?: boolean;
  below_cost_reason?: string;
}

export interface CreateFeeScheduleInput extends FeeRateInput {
  effective_from: string;
  effective_to?: string;
}

/**
 * Both fee-schedule writes change what can price a settlement, so both
 * invalidate the merchant's windows and the gap list that drives the banner.
 */
function invalidatePricing(queryClient: ReturnType<typeof useQueryClient>, merchantId?: string) {
  if (merchantId) {
    void queryClient.invalidateQueries({ queryKey: keys.feeSchedules.detail(merchantId) });
  } else {
    void queryClient.invalidateQueries({ queryKey: keys.feeSchedules.all });
    void queryClient.invalidateQueries({ queryKey: ["fee-schedule"] });
  }
  void queryClient.invalidateQueries({ queryKey: keys.feeGaps.all });
}

/**
 * POST /admin/merchants/:id/fee-schedules. silenceErrorToast: the two
 * realistic failures are answered on the form -- 409 for an overlapping
 * window, 422 below_cost by asking for the override.
 */
export function useCreateFeeSchedule(merchantId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    meta: { silenceErrorToast: true },
    mutationFn: (input: CreateFeeScheduleInput) =>
      api.post<FeeSchedule>(`/merchants/${encodeURIComponent(merchantId)}/fee-schedules`, input),
    onSuccess: () => invalidatePricing(queryClient, merchantId),
  });
}

/** POST /admin/merchants/:id/fee-schedules/:fee_schedule_id/close. The one
 *  mutation a window allows: an end date, in the future, once. */
export function useCloseFeeSchedule(merchantId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    meta: { silenceErrorToast: true },
    mutationFn: ({ feeScheduleId, effectiveTo }: { feeScheduleId: string; effectiveTo: string }) =>
      api.post<FeeSchedule>(
        `/merchants/${encodeURIComponent(merchantId)}/fee-schedules/${encodeURIComponent(feeScheduleId)}/close`,
        { effective_to: effectiveTo },
      ),
    onSuccess: () => invalidatePricing(queryClient, merchantId),
  });
}

/** One row of GET /admin/fee-schedules/gaps. See models.FeeScheduleGapResponse. */
export interface FeeScheduleGap {
  merchant_id: string;
  merchant_name: string;
  payment_method: PaymentMethod;
  transaction_count: number;
  /** SUCCESSFULL payments still waiting to settle. */
  unsettled_count: number;
  /** A window must start no later than this to cover every waiting payment. */
  oldest_unsettled_at?: string;
  /** False: nothing prices this pair today. True: a window covers today but
   *  starts after the oldest waiting payment. */
  in_force_now: boolean;
}

/** GET /admin/fee-schedules/gaps. */
export function useFeeScheduleGaps() {
  const api = useApi();
  return useQuery({
    queryKey: keys.feeGaps.list(),
    queryFn: () => api.get<ListPayload<FeeScheduleGap>>("/fee-schedules/gaps"),
    select: (payload) => payload.items,
    retry: retryTransportOnly,
  });
}

export interface BackfillFeeSchedulesInput {
  effective_from: string;
  rates: FeeRateInput[];
}

export interface BackfillFeeSchedulesResult {
  created: FeeSchedule[];
  skipped: { merchant_id: string; merchant_name: string; payment_method: PaymentMethod; reason: string }[];
  remaining_gaps: FeeScheduleGap[];
}

/** POST /admin/fee-schedules/backfill. One window per gap; all or nothing. */
export function useBackfillFeeSchedules() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    meta: { silenceErrorToast: true },
    mutationFn: (input: BackfillFeeSchedulesInput) =>
      api.post<BackfillFeeSchedulesResult>("/fee-schedules/backfill", input),
    onSuccess: () => invalidatePricing(queryClient),
  });
}

/*
 * Merchant callback configuration (PRO-1028). "Register a webhook", from the
 * ops side.
 */

/** GET /admin/storefronts/:id/callback. See admin.CallbackConfigView. */
export interface CallbackConfigView {
  callback_url: string | null;
  enabled: boolean;
  has_secret: boolean;
  /** The tail of the signing secret. Enough to tell two rotations apart,
   *  useless for forging a signature. */
  secret_hint?: string;
  secret_rotated_at?: string;
  /**
   * Present ONLY in the response that mints it, which is the first time a URL
   * is set on a storefront with no secret. Never returned by a GET, never
   * recoverable afterwards. Show it through <OneTimeSecret/> and nowhere else.
   */
  signing_secret?: string;
  /** What DELETE would cancel. The number the clear confirmation states. */
  pending_deliveries: number;
}

/** DELETE /admin/storefronts/:id/callback. See admin.ClearCallbackResult. */
export interface ClearCallbackResult {
  cancelled_deliveries: number;
}

export interface SetCallbackInput {
  callback_url: string;
  /** Omitted means enabled. Sent explicitly so "off" stays distinguishable
   *  from "not supplied" — the server makes the same distinction. */
  enabled?: boolean;
}

/** GET /admin/storefronts/:id/callback. */
export function useStorefrontCallback(storefrontId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.callbackConfig.detail(storefrontId),
    queryFn: () =>
      api.get<CallbackConfigView>(`/storefronts/${encodeURIComponent(storefrontId)}/callback`),
    retry: retryTransportOnly,
  });
}

/**
 * PUT /admin/storefronts/:id/callback.
 *
 * A tier-1 write: it registers a destination and destroys nothing. The one
 * consequence worth care is that a first set MINTS the signing secret and
 * returns the plaintext once — see CallbackConfigView.signing_secret.
 *
 * silenceErrorToast: the realistic failure is a rejected URL, and
 * pkg/callbackurl's message ("callback URL points at a non-public address:
 * 127.0.0.1") belongs next to the field that holds it, not in a toast.
 */
export function useSetStorefrontCallback(storefrontId: string) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    meta: { silenceErrorToast: true },
    mutationFn: (input: SetCallbackInput) =>
      api.put<CallbackConfigView>(
        `/storefronts/${encodeURIComponent(storefrontId)}/callback`,
        input,
      ),
    onSuccess: (config) => {
      queryClient.setQueryData(keys.callbackConfig.detail(storefrontId), config);
      // The storefront detail and list both render callback state from their
      // own copy of the row, so neither can be left showing "not configured".
      void queryClient.invalidateQueries({ queryKey: keys.storefronts.all });
      void queryClient.invalidateQueries({
        queryKey: keys.storefronts.detail(storefrontId),
      });
    },
  });
}

/**
 * DELETE /admin/storefronts/:id/callback.
 *
 * Clears the destination AND the signing secret, and cancels every delivery
 * still queued for the storefront. The last part is the one nobody expects, so
 * the count comes back in the response and the dialog states it beforehand.
 *
 * The delivery caches are invalidated too: rows this just moved to `cancelled`
 * are on screen elsewhere.
 */
export function useClearStorefrontCallback(storefrontId: string) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    meta: { silenceErrorToast: true },
    mutationFn: () =>
      api.del<ClearCallbackResult>(`/storefronts/${encodeURIComponent(storefrontId)}/callback`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.callbackConfig.all });
      void queryClient.invalidateQueries({ queryKey: keys.storefronts.all });
      void queryClient.invalidateQueries({
        queryKey: keys.storefronts.detail(storefrontId),
      });
      void queryClient.invalidateQueries({ queryKey: keys.callbacks.all });
    },
  });
}

/**
 * POST /admin/storefronts/:id/callback/rotate-secret.
 *
 * Tier-2: there is no grace window, and the response carries the new
 * plaintext exactly once, same as the first-set mint. silenceErrorToast for
 * the same reason as the other two mutations on this resource -- a failure
 * here is realistically ErrEncryptionUnavailable, which is a page-level
 * concern, not a toast.
 */
export function useRotateCallbackSecret(storefrontId: string) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    meta: { silenceErrorToast: true },
    mutationFn: () =>
      api.post<CallbackConfigView>(
        `/storefronts/${encodeURIComponent(storefrontId)}/callback/rotate-secret`,
      ),
    onSuccess: (config) => {
      queryClient.setQueryData(keys.callbackConfig.detail(storefrontId), config);
      void queryClient.invalidateQueries({ queryKey: keys.storefronts.detail(storefrontId) });
    },
  });
}

/*
 * Webhook events (PRO-1023). The inbound half of the debugging story: what the
 * provider told us, and what we did about it.
 */

/** One row of GET /admin/webhook-events. See admin.WebhookEventListItem. */
export interface WebhookEventListItem {
  id: string;
  source: string;
  webhook_type: string;
  /** Whatever DurianPay put in the payload. The set is theirs and undocumented. */
  event_type?: string;
  endpoint: string;
  ip_address?: string;
  external_id?: string;
  /** Null means the event could not be deduplicated -- see the detail screen. */
  dedupe_key?: string;
  order_ref_id?: string;
  /** False both when order_ref_id is null and when it names an order we do not
   *  have. Link only when true: a 404 mid-incident reads as a missing order. */
  order_ref_resolved: boolean;
  /** DurianPay's redelivery count. A staff retry does not change it. */
  retry_count: number;
  /** The signature check's verdict on arrival. Absent on rows before it ran. */
  signature_status?: "valid" | "invalid" | "missing" | "skipped";
  status: string;
  /** On the list as well as the detail: the screen defaults to failed events,
   *  and an error readable without opening a row saves twenty clicks. */
  processing_error?: string;
  /** "ignored:<reason>" when processing acknowledged the webhook without
   *  applying it (PRO-1345): one payment attempt, not the transaction. */
  outcome?: string;
  processed_at?: string;
  received_at: string;
  /** Whether POST .../retry would run (PRO-1203): failed, signature not
   *  invalid, body parsed. Computed server-side so the button and the guarded
   *  claim agree. */
  retryable: boolean;
}

/** GET /admin/webhook-events/:id. See admin.WebhookEventDetail. */
export interface WebhookEventDetail extends WebhookEventListItem {
  /** The stored payload, whole and unmodified. What gets pasted into a
   *  DurianPay support thread. */
  raw_payload: Record<string, unknown> | null;
  /** Credential-shaped header values are replaced server-side with
   *  "[redacted]"; X-SIGNATURE and friends are not. See admin/redact.go. */
  headers: Record<string, unknown> | null;
  /** True when at least one header value was hidden. Say so on the page rather
   *  than presenting an edited blob as the bytes that arrived. */
  headers_redacted: boolean;
  created_at: string;
  updated_at: string;
}

export type WebhookEventFilters = {
  source?: string;
  webhook_type?: string;
  event_type?: string;
  status?: string;
  /** A prefix match on the server. */
  order_ref_id?: string;
  /** A prefix match on the server. */
  external_id?: string;
  /** An exact match on the server. */
  endpoint?: string;
  /** RFC 3339 or a bare YYYY-MM-DD; a bare end_date means end of that day. */
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

/** GET /admin/webhook-events. */
export function useWebhookEvents(filters: WebhookEventFilters) {
  const api = useApi();
  return useQuery({
    queryKey: keys.webhookEvents.list(filters),
    queryFn: () =>
      api.get<ListPayload<WebhookEventListItem>>(`/webhook-events${queryString(filters)}`),
    placeholderData: keepPreviousData,
    retry: retryTransportOnly,
  });
}

/** GET /admin/webhook-events/:id. */
export function useWebhookEvent(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.webhookEvents.detail(id),
    queryFn: () => api.get<WebhookEventDetail>(`/webhook-events/${encodeURIComponent(id)}`),
    retry: retryTransportOnly,
  });
}

/**
 * POST /admin/webhook-events/:id/retry (PRO-1203). Re-runs a failed event from
 * its stored payload. A 200 does not mean it worked: the returned event's
 * status is processed or failed, and the caller says which.
 *
 * A retried settlement or payment moves transactions, settlements and the
 * order's timeline, so those lists are invalidated along with the events.
 *
 * silenceErrorToast: the realistic error is a 409 (already retried, or
 * DurianPay redelivered first), answered next to the button.
 */
export function useRetryWebhookEvent(eventId: string) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    meta: { silenceErrorToast: true },
    mutationFn: () =>
      api.post<WebhookEventDetail>(`/webhook-events/${encodeURIComponent(eventId)}/retry`),
    onSuccess: (event) => {
      queryClient.setQueryData(keys.webhookEvents.detail(eventId), event);
      void queryClient.invalidateQueries({ queryKey: keys.webhookEvents.all });
      void queryClient.invalidateQueries({ queryKey: keys.transactions.all });
      void queryClient.invalidateQueries({ queryKey: keys.settlements.all });
      if (event.order_ref_id) {
        void queryClient.invalidateQueries({ queryKey: keys.timeline.detail(event.order_ref_id) });
        void queryClient.invalidateQueries({ queryKey: keys.transactions.detail(event.order_ref_id) });
      }
    },
  });
}

/*
 * Callback deliveries (PRO-1025). The outbound half: Altura knows a payment
 * succeeded -- did the merchant ever find out?
 */

/** One row of GET /admin/callback-deliveries. See admin.CallbackDeliveryListItem. */
export interface CallbackDeliveryListItem {
  id: string;
  storefront_id: string;
  /** Not nullable: the foreign key is ON DELETE CASCADE, so the join cannot miss. */
  storefront_name: string;
  order_id: string;
  /** The merchant-facing event name, not the internal payment_status. */
  event_type: string;
  status: string;
  attempt_count: number;
  max_attempts: number;
  /** Frozen at enqueue. A URL that no longer matches the storefront's current
   *  config is correct behaviour, not a bug -- the page says so. */
  target_url: string;
  last_status_code?: number;
  last_error?: string;
  last_attempt_at?: string;
  delivered_at?: string;
  /** Absent once the delivery is terminal: the column still holds a value, and
   *  showing it would promise a retry that is never coming. */
  next_attempt_at?: string;
  /** The worker's lease. Older than five minutes means a worker died and
   *  housekeeping (hourly) has not swept it yet. Merchants never see this. */
  locked_at?: string;
  created_at: string;
}

/** One HTTP attempt. See admin.CallbackDeliveryAttemptView. */
export interface CallbackDeliveryAttemptView {
  attempt_number: number;
  started_at: string;
  duration_ms?: number;
  /** Absent when the request never got a response -- a transport failure has an
   *  error and no status code. Both shapes have to render. */
  status_code?: number;
  /** The first 2 KiB of what the merchant returned. */
  response_body?: string;
  error?: string;
  /** hostname:pid. "Every failure came from one worker" is a different
   *  diagnosis from "they came from all of them". */
  worker_id?: string;
}

/** GET /admin/callback-deliveries/:id. See admin.CallbackDeliveryDetail. */
export interface CallbackDeliveryDetail extends CallbackDeliveryListItem {
  /** The exact body sent on every attempt, byte-identical, frozen at enqueue. */
  payload: Record<string, unknown> | null;
  locked_by?: string;
  updated_at: string;
  attempts: CallbackDeliveryAttemptView[];
  /** Whether a replay would do anything. True only for exhausted and cancelled;
   *  computed server-side so the button and the guarded UPDATE agree. */
  replayable: boolean;
  /** The storefront's callback config as it stands NOW, against which the frozen
   *  snapshot above is read. All three change what a replay will do. */
  storefront_callback_url?: string;
  storefront_callback_enabled: boolean;
  /** Later than created_at means a replay signs with a secret the merchant may
   *  never have seen — signing is per-attempt, so a rotation reaches old rows. */
  storefront_callback_secret_rotated_at?: string;
}

/** POST /admin/callback-deliveries/:id/replay. See admin.ReplayResult. */
export interface ReplayResult {
  delivery: CallbackDeliveryDetail;
  /** False means SQS was not told — not a failure. The worker's hourly sweep
   *  claims due rows no message arrived for; it costs latency, not delivery. */
  signalled: boolean;
}

export type CallbackDeliveryFilters = {
  storefront_id?: string;
  /** A prefix match on the server. */
  order_id?: string;
  status?: string;
  event_type?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

/** GET /admin/callback-deliveries. Cross-storefront, unlike the v1 endpoint. */
export function useCallbackDeliveries(filters: CallbackDeliveryFilters) {
  const api = useApi();
  return useQuery({
    queryKey: keys.callbacks.list(filters),
    queryFn: () =>
      api.get<ListPayload<CallbackDeliveryListItem>>(`/callback-deliveries${queryString(filters)}`),
    placeholderData: keepPreviousData,
    retry: retryTransportOnly,
  });
}

/** GET /admin/callback-deliveries/:id, with the full attempt history. */
export function useCallbackDelivery(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.callbacks.detail(id),
    queryFn: () =>
      api.get<CallbackDeliveryDetail>(`/callback-deliveries/${encodeURIComponent(id)}`),
    retry: retryTransportOnly,
  });
}

/**
 * POST /admin/callback-deliveries/:id/replay (PRO-1027).
 *
 * A tier-1 write: it re-queues a notification the merchant should have had
 * anyway. The server resets the scheduling columns and the existing worker does
 * the rest — there is no second delivery path, so nothing here needs to know
 * about signing, backoff or the SSRF guard.
 *
 * Three caches go stale at once and all three are visible from this page: the
 * delivery itself, every filtered variant of the list (the row leaves the
 * default `exhausted` view), and the order's timeline, which gains an entry as
 * soon as the worker makes the attempt.
 *
 * silenceErrorToast: the realistic failure is a 409 — someone else replayed it,
 * or the worker picked it up between the page loading and the button being
 * pressed — and that is answered on the card, next to the status it contradicts.
 */
export function useReplayCallbackDelivery(deliveryId: string) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    meta: { silenceErrorToast: true },
    mutationFn: () =>
      api.post<ReplayResult>(`/callback-deliveries/${encodeURIComponent(deliveryId)}/replay`),
    onSuccess: (result) => {
      queryClient.setQueryData(keys.callbacks.detail(deliveryId), result.delivery);
      void queryClient.invalidateQueries({ queryKey: keys.callbacks.all });
      void queryClient.invalidateQueries({
        queryKey: keys.timeline.detail(result.delivery.order_id),
      });
    },
  });
}

/*
 * Global search (PRO-1033). Paste any identifier, land on the right page.
 */

/** Which screen a result belongs to. See the admin.SearchType* constants. */
export type SearchResultType =
  | "transaction"
  | "bridge_order"
  | "webhook_event"
  | "storefront"
  | "settlement";

/** One thing the query might have meant. See admin.SearchResult. */
export interface SearchResult {
  type: SearchResultType;
  /** The route param for `type`'s detail screen. */
  id: string;
  label: string;
  sublabel?: string;
  /** The column that matched. "found by reference_no" is a different fact from
   *  "found by order_id", and the searcher usually knows which they pasted. */
  matched_on: string;
}

/** GET /admin/search. See admin.SearchResponse. */
export interface SearchResponse {
  results: SearchResult[];
  total: number;
  /** A branch hit its cap — do not read `total` as "exactly this many exist". */
  truncated: boolean;
  /**
   * The query, echoed so a stale response can be discarded.
   *
   * Never the raw input: when a whole API token was pasted, the server returns
   * only its public prefix. Do not render this expecting it to equal what was
   * typed.
   */
  query: string;
}

/**
 * GET /admin/search.
 *
 * Enabled only past the server's own minimum length, so an empty box issues no
 * request at all. Kept briefly in cache: reopening the palette on the same
 * string within a few seconds is a very common thing to do, and re-running six
 * indexed lookups for it is waste.
 */
export function useSearch(query: string) {
  const api = useApi();
  const trimmed = query.trim();

  return useQuery({
    queryKey: keys.search.list({ q: trimmed }),
    queryFn: () => api.get<SearchResponse>(`/search?q=${encodeURIComponent(trimmed)}`),
    enabled: trimmed.length >= 3,
    staleTime: 10_000,
    retry: retryTransportOnly,
  });
}

/*
 * The Shopify bridge (PRO-1032). How Arutala actually works: a Shopify order
 * becomes an Altura payment, and loyalty points get applied against it.
 *
 * MONEY WARNING. bridge_orders.amount_gross / amount_net and
 * bridge_payments.amount_requested / amount_paid are bigint MINOR units on the
 * server; transactions.amount is a float in MAJOR units. Both arrive here as
 * decimal strings already converted, so formatIDR is correct for all of them —
 * but never do arithmetic across the two on the client, and never assume a
 * bridge number and a transaction number came through the same path.
 */

/** One row of GET /admin/bridge/orders. See admin.BridgeOrderListItem. */
export interface BridgeOrderListItem {
  shopify_order_id: string;
  /** What a merchant quotes, e.g. "#1042". The id above is what the API uses. */
  shopify_order_name: string;
  shopify_customer_id: string;
  customer_email: string;
  currency: string;
  /** Decimal strings, converted server-side from minor units. */
  amount_gross: string;
  amount_net: string;
  /** One of eight values, and its own vocabulary — see BridgeOrderDetail. */
  status: string;
  created_at: string;
  updated_at: string;
}

/** One payment attempt. See admin.BridgePaymentView. */
export interface BridgePaymentView {
  /** Both this row's key and the join to transactions.order_id. */
  altura_transaction_id: string;
  payment_url?: string;
  provider?: string;
  method?: string;
  amount_requested: string;
  amount_paid: string;
  paid_at?: string;
  /** The second vocabulary: INITIATED | COMPLETED | FAILED | … */
  status: string;
  created_at: string;
  /** False when altura_transaction_id resolves to nothing — the bridge believes
   *  it created a transaction and there is none. A finding, not an error. */
  transaction_found: boolean;
  /** The third vocabulary: INITIATED | SUCCESSFULL | SETTLED | … */
  transaction_status?: string;
  transaction_amount?: string;
}

/**
 * One points application. See admin.PointsRedemptionView.
 *
 * Displayed, never interpreted. The loyalty ledger lives in Semprong/Swivel;
 * this table is only Altura's record that an offset was applied.
 */
export interface PointsRedemptionView {
  id: string;
  customer_email?: string;
  points_used: number;
  /** Points-to-currency. A rate, not money — do not run it through formatIDR. */
  rate: number;
  /** Minor units on the column; a decimal string here. */
  amount_offset: string;
  created_at: string;
}

/** GET /admin/bridge/orders/:shopify_order_id. See admin.BridgeOrderDetail. */
export interface BridgeOrderDetail extends BridgeOrderListItem {
  payments: BridgePaymentView[];
  points: PointsRedemptionView[];
  /** False when the three vocabularies disagree about whether this payment
   *  succeeded. Computed server-side; the screen states what it means. */
  statuses_agree: boolean;
}

export type BridgeOrderFilters = {
  status?: string;
  /** Exact matches on the server, both of them. */
  customer_email?: string;
  shopify_customer_id?: string;
  /** A prefix match. */
  shopify_order_name?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

/** GET /admin/bridge/orders. */
export function useBridgeOrders(filters: BridgeOrderFilters) {
  const api = useApi();
  return useQuery({
    queryKey: keys.bridgeOrders.list(filters),
    queryFn: () =>
      api.get<ListPayload<BridgeOrderListItem>>(`/bridge/orders${queryString(filters)}`),
    placeholderData: keepPreviousData,
    retry: retryTransportOnly,
  });
}

/** GET /admin/bridge/orders/:shopify_order_id, with payments and points. */
export function useBridgeOrder(shopifyOrderId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.bridgeOrders.detail(shopifyOrderId),
    queryFn: () =>
      api.get<BridgeOrderDetail>(`/bridge/orders/${encodeURIComponent(shopifyOrderId)}`),
    retry: retryTransportOnly,
  });
}

/*
 * Finance (PRO-1031). Settlements and disbursements, read-only.
 *
 * Platform-global: neither table has a storefront_id. These are DurianPay
 * settling into Kugie's account and payouts back out of it, and the per-merchant
 * split does not exist at this layer.
 */

/**
 * One row of GET /admin/settlements. See admin.SettlementListItem.
 *
 * Almost everything is optional, and that is the live schema rather than
 * defensive typing: migrations/000 has psp_settlement_id, original_amount,
 * settlement_fee, currency, status and settlement_date all nullable, while the
 * Go model declares them NOT NULL. Render absent as an em dash — never as
 * "Rp 0", which is a number a finance person will act on.
 */
export interface SettlementListItem {
  settlement_id: string;
  psp_settlement_id?: string;
  /** Decimal strings. Only settlement_amount is guaranteed present. */
  original_amount?: string;
  settlement_amount: string;
  settlement_fee?: string;
  currency?: string;
  /** A free-form varchar, not an enum. Any value can appear here. */
  status?: string;
  settlement_date?: string;
  created_at?: string;
  /** How many orders this settlement covers. A count, never a sum. */
  transaction_count: number;
  /** True when this psp_settlement_id is on more than one settlement row. The
   *  model claims a unique index the database does not have. */
  duplicate_psp_id?: boolean;
}

/** One order a settlement covers. See admin.SettledOrder. */
export interface SettledOrder {
  order_id: string;
  storefront_id?: string;
  storefront_name?: string;
  amount?: string;
  payment_status?: string;
  payment_method?: string;
  created_at?: string;
  /** False when the junction row outlived its transaction — the foreign key is
   *  ON DELETE SET NULL, so it is a state the schema permits. */
  transaction_found: boolean;
}

/** GET /admin/settlements/:id. See admin.SettlementDetail. */
export interface SettlementDetail extends SettlementListItem {
  updated_at?: string;
  orders: SettledOrder[];
}

export type SettlementFilters = {
  /** A prefix match on the server. */
  psp_settlement_id?: string;
  /** An exact match. Free text: the column is an unconstrained varchar. */
  status?: string;
  /** Applied to settlement_date — the day the money moved. */
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

/** GET /admin/settlements. */
export function useSettlements(filters: SettlementFilters) {
  const api = useApi();
  return useQuery({
    queryKey: keys.settlements.list(filters),
    queryFn: () => api.get<ListPayload<SettlementListItem>>(`/settlements${queryString(filters)}`),
    placeholderData: keepPreviousData,
    retry: retryTransportOnly,
  });
}

/** GET /admin/settlements/:id, with the orders it covers. */
export function useSettlement(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.settlements.detail(id),
    queryFn: () => api.get<SettlementDetail>(`/settlements/${encodeURIComponent(id)}`),
    retry: retryTransportOnly,
  });
}

/**
 * pending -> completed | failed; both terminal (PRO-998). A pending payout has
 * already reserved the money: the merchant's balance is down by it.
 */
export type DisbursementStatus = "pending" | "completed" | "failed";

/** legacy: predates PRO-998. manual: an operator moves the money. snap_transfer: reserved. */
export type DisbursementMethod = "legacy" | "manual" | "snap_transfer";

/**
 * One row of GET /admin/disbursements. See models.DisbursementResponse --
 * the same shape every disbursement read renders since PRO-998. Mirrored by
 * hand; nothing type-checks it against the Go.
 */
export interface DisbursementListItem {
  disbursement_id: string;
  merchant_id?: string;
  /** The issued invoice this payout pays. Absent on legacy rows. */
  invoice_id?: string;
  status: DisbursementStatus;
  method: DisbursementMethod;
  disbursement_method?: string;
  /** The bank account number the money is sent to. */
  destination?: string;
  bank_code?: string;
  bank_account_holder?: string;
  /** A decimal string. Genuinely NOT NULL, unlike the settlement amounts. */
  amount: string;
  currency?: string;
  payout_journal_id?: string;
  reversal_journal_id?: string;
  psp_reference_no?: string;
  bank_reference?: string;
  failure_reason?: string;
  created_by_email?: string;
  completed_by_email?: string;
  failed_by_email?: string;
  created_at: string;
  completed_at?: string;
  failed_at?: string;
  updated_at: string;
}

/** GET /admin/disbursements/:id. See admin.DisbursementDetail. */
export interface DisbursementDetail extends DisbursementListItem {
  /** The params jsonb whole. Unstructured on legacy rows. */
  params?: Record<string, unknown> | null;
}

export type DisbursementFilters = {
  /** Exact matches. */
  merchant_id?: string;
  status?: DisbursementStatus;
  /** Prefix matches on the server. Both columns are free text. */
  method?: string;
  destination?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

/** GET /admin/disbursements. */
export function useDisbursements(filters: DisbursementFilters) {
  const api = useApi();
  return useQuery({
    queryKey: keys.disbursements.list(filters),
    queryFn: () =>
      api.get<ListPayload<DisbursementListItem>>(`/disbursements${queryString(filters)}`),
    placeholderData: keepPreviousData,
    retry: retryTransportOnly,
  });
}

/** GET /admin/disbursements/:id. */
export function useDisbursement(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.disbursements.detail(id),
    queryFn: () => api.get<DisbursementDetail>(`/disbursements/${encodeURIComponent(id)}`),
    retry: retryTransportOnly,
  });
}

/*
 * Settlement statements and the payouts that pay them (PRO-997/998, screens
 * PRO-1400). The API calls a statement an invoice: it is Kugie's commercial
 * invoice for its fee and the PPN on it, and the statement of what Kugie
 * transfers for the period. The screens say "statement"; the wire keeps
 * `invoice`, and so does everything in this section.
 */

/** draft -> issued | void, issued -> paid | void. Enforced by trigger. */
export type InvoiceStatus = "draft" | "issued" | "paid" | "void";

/** One side of the document. See models.InvoiceParty. */
export interface InvoiceParty {
  name: string;
  npwp?: string;
  address?: string;
}

/**
 * The arithmetic, every figure a decimal string. See models.InvoiceTotals for
 * the identities: net_payable = payable_credited - ppn = gross - total_due.
 */
export interface InvoiceTotals {
  gross: string;
  psp_fee: string;
  settled: string;
  platform_fee: string;
  dpp: string;
  ppn: string;
  total_due: string;
  payable_credited: string;
  net_payable: string;
}

/** One line: one payment method priced by one fee schedule window. */
export interface InvoiceLine {
  line_no: number;
  description: string;
  payment_method: PaymentMethod;
  fee_schedule_id?: string;
  /** Payments on the line, net of reversals. */
  journal_count: number;
  gross: string;
  psp_fee: string;
  platform_fee: string;
  ppn_rule_id: string;
  ppn_tarif_bps: number;
  dpp_numerator: number;
  dpp_denominator: number;
  dpp: string;
  ppn: string;
}

/** One row of GET /admin/merchants/:id/invoices. See models.InvoiceListItem. */
export interface InvoiceListItem {
  document_type: string;
  is_faktur_pajak: false;
  notice: string;
  invoice_id: string;
  /** Null until issued. */
  invoice_number: string | null;
  status: InvoiceStatus;
  merchant_id: string;
  currency: string;
  /** Both bounds inclusive. */
  period_from: string;
  period_to: string;
  issuer: InvoiceParty;
  bill_to: InvoiceParty;
  totals: InvoiceTotals;
  ppn_journal_id?: string;
  void_journal_id?: string;
  paid_disbursement_id?: string;
  created_by_email?: string;
  created_at: string;
  issued_at?: string;
  paid_at?: string;
  voided_at?: string;
  voided_by_email?: string;
  void_reason?: string;
}

/** GET /admin/merchants/:id/invoices/:invoice_id. See models.InvoiceResponse. */
export interface InvoiceDetail extends InvoiceListItem {
  lines: InvoiceLine[];
  covered_journal_ids: string[];
}

/** A keyset page, as the invoice list renders it. See models.KeysetPage. */
interface KeysetPage<T> {
  items: T[];
  next_cursor: string | null;
  limit: number;
}

/** The balance half of GET /admin/merchants/:id/ledger. */
export interface MerchantBalance {
  merchant_id: string;
  /** What Kugie owes the merchant now. A pending payout is already off it. */
  payable: string;
  currency: string;
  updated_at?: string;
  /** False when the projection disagrees with the entries. */
  verified: boolean;
}

function merchantPath(merchantId: string, rest = "") {
  return `/merchants/${encodeURIComponent(merchantId)}${rest}`;
}

function invoicePath(merchantId: string, invoiceId: string, rest = "") {
  return merchantPath(merchantId, `/invoices/${encodeURIComponent(invoiceId)}${rest}`);
}

/**
 * GET /admin/merchants/:id/invoices, newest first. One page of 100: at one
 * statement a fortnight that is four years, and the list grows a "more"
 * button the day a merchant gets there.
 */
export function useInvoices(merchantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.invoices.list({ merchantId }),
    queryFn: () => api.get<KeysetPage<InvoiceListItem>>(merchantPath(merchantId, "/invoices?limit=100")),
    retry: retryTransportOnly,
  });
}

/** GET /admin/merchants/:id/invoices/:invoice_id: the header plus its lines. */
export function useInvoice(merchantId: string, invoiceId: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: keys.invoices.detail(invoiceId),
    queryFn: () => api.get<InvoiceDetail>(invoicePath(merchantId, invoiceId)),
    enabled,
    retry: retryTransportOnly,
  });
}

/** GET /admin/merchants/:id/ledger, for the balance only. */
export function useMerchantBalance(merchantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.merchantLedger.detail(merchantId),
    queryFn: () => api.get<{ balance: MerchantBalance }>(merchantPath(merchantId, "/ledger?limit=1")),
    select: (payload) => payload.balance,
    retry: retryTransportOnly,
  });
}

/**
 * GET /admin/merchants/:id/invoices/:invoice_id/document: the printable page,
 * server-rendered HTML. Fetched rather than linked because the route needs
 * the staff bearer token, which a plain link cannot carry.
 */
export function useFetchInvoiceDocument(merchantId: string) {
  const api = useApi();
  return (invoiceId: string) => api.getText(invoicePath(merchantId, invoiceId, "/document"));
}

/**
 * Every statement and payout write moves the same things: the statement list
 * and the document, the payouts, the balance, and the audit log. Invalidated
 * on settle, not success -- a 409 means someone else moved the row, and the
 * screen should show where it is now.
 */
function invalidateStatements(
  queryClient: ReturnType<typeof useQueryClient>,
  merchantId: string,
  invoiceId?: string,
) {
  void queryClient.invalidateQueries({ queryKey: keys.invoices.all });
  if (invoiceId) void queryClient.invalidateQueries({ queryKey: keys.invoices.detail(invoiceId) });
  void queryClient.invalidateQueries({ queryKey: keys.disbursements.all });
  // Detail keys are ["disbursement", id], outside the list prefix.
  void queryClient.invalidateQueries({ queryKey: ["disbursement"] });
  void queryClient.invalidateQueries({ queryKey: keys.merchantLedger.detail(merchantId) });
  void queryClient.invalidateQueries({ queryKey: keys.auditLog.all });
}

/** POST /admin/merchants/:id/invoices. Both bounds inclusive, RFC 3339. */
export function useCreateInvoice(merchantId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    meta: { silenceErrorToast: true },
    mutationFn: (input: { period_from: string; period_to: string }) =>
      api.post<InvoiceDetail>(merchantPath(merchantId, "/invoices"), input),
    onSettled: (created) => invalidateStatements(queryClient, merchantId, created?.invoice_id),
  });
}

/** POST .../invoices/:invoice_id/issue: number it and book its PPN. */
export function useIssueInvoice(merchantId: string, invoiceId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    meta: { silenceErrorToast: true },
    mutationFn: () => api.post<InvoiceDetail>(invoicePath(merchantId, invoiceId, "/issue")),
    onSettled: () => invalidateStatements(queryClient, merchantId, invoiceId),
  });
}

/** POST .../invoices/:invoice_id/void. A reason is required once issued. */
export function useVoidInvoice(merchantId: string, invoiceId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    meta: { silenceErrorToast: true },
    mutationFn: (reason: string) =>
      api.post<InvoiceDetail>(invoicePath(merchantId, invoiceId, "/void"), { reason }),
    onSettled: () => invalidateStatements(queryClient, merchantId, invoiceId),
  });
}

/**
 * POST /admin/merchants/:id/disbursements: a manual payout for exactly the
 * statement's net payable, reserved off the balance at once. The money moves
 * when someone makes the bank transfer; this only records that it will.
 */
export function usePayInvoice(merchantId: string, invoiceId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    meta: { silenceErrorToast: true },
    mutationFn: () =>
      api.post<DisbursementListItem>(merchantPath(merchantId, "/disbursements"), {
        invoice_id: invoiceId,
        method: "manual",
      }),
    onSettled: () => invalidateStatements(queryClient, merchantId, invoiceId),
  });
}

/** POST /admin/disbursements/:id/complete: the transfer was made. Marks the
 *  statement paid. */
export function useCompleteDisbursement(merchantId: string, invoiceId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    meta: { silenceErrorToast: true },
    mutationFn: ({ disbursementId, bankReference }: { disbursementId: string; bankReference: string }) =>
      api.post<DisbursementListItem>(`/disbursements/${encodeURIComponent(disbursementId)}/complete`, {
        bank_reference: bankReference,
      }),
    onSettled: () => invalidateStatements(queryClient, merchantId, invoiceId),
  });
}

/** POST /admin/disbursements/:id/fail: the transfer did not happen. The
 *  reserve goes back on the balance and the statement can be paid again. */
export function useFailDisbursement(merchantId: string, invoiceId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    meta: { silenceErrorToast: true },
    mutationFn: ({ disbursementId, reason }: { disbursementId: string; reason: string }) =>
      api.post<DisbursementListItem>(`/disbursements/${encodeURIComponent(disbursementId)}/fail`, { reason }),
    onSettled: () => invalidateStatements(queryClient, merchantId, invoiceId),
  });
}

/*
 * Provider status (PRO-1029/PRO-1030). The one question Altura's own tables
 * cannot answer: we say pending, what does DurianPay say?
 */

/** Why no lookup was made. Each needs its own sentence on screen. */
export type ProviderStatusReason = "no_psp_identifiers" | "provider_not_configured";

/** What the two sides add up to, computed server-side. See admin.Verdict. */
export type ProviderVerdict =
  | "agree"
  | "provider_ahead"
  | "altura_ahead"
  | "amount_mismatch"
  | "no_provider_record"
  | "unknown"
  | "not_checked";

/** See admin.ProviderStatusResult. */
export interface ProviderStatusResult {
  order_id: string;
  /** When we asked. The panel stamps itself with this and lets it go stale. */
  checked_at: string;
  altura: {
    payment_status: string;
    /** A decimal string, never a number. Format it with formatIDR. */
    amount: string;
    updated_at: string;
  };
  /** False means no lookup happened; `reason` says why. Not an error. */
  available: boolean;
  reason?: ProviderStatusReason;
  provider?: {
    /** "order" or "payment" — an order can exist while no payment does. */
    kind: string;
    ref: string;
    /** DurianPay's own vocabulary, never translated into ours. */
    status: string;
    /** Absent when the provider sent an amount we could not read
     *  unambiguously; the panel then declines to compare rather than
     *  inventing a mismatch. */
    amount?: string;
    currency?: string;
    created_at?: string;
    updated_at?: string;
    raw?: Record<string, unknown>;
  };
  verdict: ProviderVerdict;
  /** What we had to ask with, so "no record" reads against the id used. */
  identifiers: Record<string, string>;
}

/**
 * GET /admin/transactions/:order_id/provider-status.
 *
 * A mutation rather than a query, deliberately, even though the endpoint is a
 * GET. Every call is an outbound request to DurianPay, so it must happen only
 * when someone presses the button: useQuery would cache the answer, refetch it
 * on mount, and turn idle browsing into load on a partner. useMutation gives no
 * cache entry, no refetch, and one call per click — which is the honest shape
 * for "what does the provider say right now".
 *
 * silenceErrorToast: the failures here are specific (rate limited, provider
 * unreachable) and belong on the panel next to the comparison they replace.
 */
export function useProviderStatus(orderId: string) {
  const api = useApi();
  return useMutation({
    meta: { silenceErrorToast: true },
    mutationFn: () =>
      api.get<ProviderStatusResult>(
        `/transactions/${encodeURIComponent(orderId)}/provider-status`,
      ),
  });
}

/*
 * The unified order timeline (PRO-1026). One chronology merged server-side from
 * webhook_events, transactions and callback_delivery_attempts.
 */

/** Which table an entry came from. Drives the timeline's colour coding. */
export type TimelineSource = "transaction" | "webhook" | "callback";

/** One moment in an order's life. See admin.TimelineEntry. */
export interface TimelineEntry {
  timestamp: string;
  source: TimelineSource;
  /** order_created | webhook_received | callback_attempt | … The UI branches on
   *  this, never on the summary text. */
  kind: string;
  summary: string;
  /**
   * Deduced rather than recorded, and rendered as such.
   *
   * There is no status-transition history in this database:
   * transactions.payment_status is a single mutable column that UpdateStatusIf
   * overwrites, so a webhook's arrival is evidence a status probably changed
   * then, not a record that it did.
   */
  inferred: boolean;
  detail?: Record<string, unknown>;
}

/** GET /admin/orders/:order_id/timeline. See admin.OrderTimeline. */
export interface OrderTimeline {
  order_id: string;
  /** False when webhooks or callbacks reference an order with no transaction
   *  row -- a finding, not an error, so this is not a 404. */
  transaction_found: boolean;
  current_status?: string;
  entries: TimelineEntry[];
  truncated: boolean;
}

/** GET /admin/orders/:order_id/timeline. */
export function useOrderTimeline(orderId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.timeline.detail(orderId),
    queryFn: () =>
      api.get<OrderTimeline>(`/orders/${encodeURIComponent(orderId)}/timeline`),
    retry: retryTransportOnly,
  });
}

/*
 * API key management (PRO-1036): mint, list and revoke a storefront's keys
 * from the dashboard instead of a curl command against the platform routes.
 */

/** One row of GET /admin/storefronts/:id/api-keys. See
 *  models.APIKeyResponse. */
export interface APIKeyResponse {
  id: string;
  storefront_id: string;
  key_prefix: string;
  name?: string;
  active: boolean;
  created_at: string;
  /** Touched at most every 5 minutes by the authenticating request, so this
   *  can be up to that stale. Never presented as exact. */
  last_used_at?: string;
  expires_at?: string;
  revoked_at?: string;
  revoked_reason?: string;
}

/** POST .../api-keys. The only response that ever carries the token. */
export interface CreateAPIKeyResult extends APIKeyResponse {
  key: string;
}

export interface CreateAPIKeyInput {
  name?: string;
  expires_in_days?: number;
}

export interface RevokeAPIKeyInput {
  reason?: string;
}

/** GET /admin/storefronts/:id/api-keys. A plain array — a storefront's key
 *  count is small by construction, so there is no pagination envelope here. */
export function useStorefrontAPIKeys(storefrontId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.apiKeys.detail(storefrontId),
    queryFn: () =>
      api.get<APIKeyResponse[]>(`/storefronts/${encodeURIComponent(storefrontId)}/api-keys`),
    retry: retryTransportOnly,
  });
}

/**
 * POST /admin/storefronts/:id/api-keys.
 *
 * A tier-1 create in effect -- it mints and destroys nothing -- but its
 * result feeds the same one-time-reveal pattern as a signing secret, so the
 * caller drives that dialog off onSuccess the same way useSetStorefrontCallback
 * does.
 */
export function useCreateAPIKey(storefrontId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    meta: { silenceErrorToast: true },
    mutationFn: (input: CreateAPIKeyInput) =>
      api.post<CreateAPIKeyResult>(
        `/storefronts/${encodeURIComponent(storefrontId)}/api-keys`,
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.apiKeys.detail(storefrontId) });
    },
  });
}

/** DELETE /admin/storefronts/:id/api-keys/:key_id. Tier-2, soft: the row
 *  stays, revoked_at and revoked_reason are set. */
export function useRevokeAPIKey(storefrontId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    meta: { silenceErrorToast: true },
    mutationFn: ({ keyId, reason }: { keyId: string } & RevokeAPIKeyInput) =>
      api.del<APIKeyResponse>(
        `/storefronts/${encodeURIComponent(storefrontId)}/api-keys/${encodeURIComponent(keyId)}`,
        { reason },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.apiKeys.detail(storefrontId) });
    },
  });
}

/*
 * Shopify credential management (PRO-1038). Four platform-only routes
 * surfaced on the storefront's Shopify tab, plus a view that names which of
 * the three real api_version defaults is actually in effect.
 */

/** GET /admin/storefronts/:id/shopify-credentials. See
 *  admin.ShopifyCredentialsView. */
export interface ShopifyCredentialsView {
  credential_type: "" | "oauth" | "static";
  oauth_configured: boolean;
  static_token_configured: boolean;
  webhook_secret_configured: boolean;
  shop_domain?: string;
  stored_api_version?: string;
  effective_api_version: string;
  effective_api_version_source: "stored" | "runtime_fallback_2026_01" | "env_fallback";
  env_fallback_active: boolean;
  env_fallback_api_version?: string;
}

export interface SetShopifyCredentialsInput {
  access_token: string;
  /** Omitted (or empty) means "leave the stored version unchanged" — this
   *  surface never invents a default the way the merchant-facing routes do. */
  api_version?: string;
  /** Omitted (or empty) means "leave unchanged". */
  webhook_secret?: string;
}

export interface SetShopifyOAuthCredentialsInput {
  client_id: string;
  client_secret: string;
  api_version?: string;
  webhook_secret?: string;
}

/** GET /admin/storefronts/:id/shopify-credentials. */
export function useStorefrontShopifyCredentials(storefrontId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.shopifyCredentials.detail(storefrontId),
    queryFn: () =>
      api.get<ShopifyCredentialsView>(
        `/storefronts/${encodeURIComponent(storefrontId)}/shopify-credentials`,
      ),
    retry: retryTransportOnly,
  });
}

function invalidateShopifyCredentials(
  queryClient: ReturnType<typeof useQueryClient>,
  storefrontId: string,
) {
  void queryClient.invalidateQueries({ queryKey: keys.shopifyCredentials.detail(storefrontId) });
  void queryClient.invalidateQueries({ queryKey: keys.storefronts.detail(storefrontId) });
}

/** PUT /admin/storefronts/:id/shopify-credentials. Tier-2: changes which
 *  path Shopify order ingestion uses for this merchant, immediately. */
export function useSetShopifyCredentials(storefrontId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    meta: { silenceErrorToast: true },
    mutationFn: (input: SetShopifyCredentialsInput) =>
      api.put<ShopifyCredentialsView>(
        `/storefronts/${encodeURIComponent(storefrontId)}/shopify-credentials`,
        input,
      ),
    onSuccess: () => invalidateShopifyCredentials(queryClient, storefrontId),
  });
}

/** DELETE /admin/storefronts/:id/shopify-credentials. Tier-2. */
export function useDeleteShopifyCredentials(storefrontId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    meta: { silenceErrorToast: true },
    mutationFn: () =>
      api.del<ShopifyCredentialsView>(
        `/storefronts/${encodeURIComponent(storefrontId)}/shopify-credentials`,
      ),
    onSuccess: () => invalidateShopifyCredentials(queryClient, storefrontId),
  });
}

/** PUT /admin/storefronts/:id/shopify-oauth-credentials. Tier-2. */
export function useSetShopifyOAuthCredentials(storefrontId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    meta: { silenceErrorToast: true },
    mutationFn: (input: SetShopifyOAuthCredentialsInput) =>
      api.put<ShopifyCredentialsView>(
        `/storefronts/${encodeURIComponent(storefrontId)}/shopify-oauth-credentials`,
        input,
      ),
    onSuccess: () => invalidateShopifyCredentials(queryClient, storefrontId),
  });
}

/** DELETE /admin/storefronts/:id/shopify-oauth-credentials. Tier-2. */
export function useDeleteShopifyOAuthCredentials(storefrontId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    meta: { silenceErrorToast: true },
    mutationFn: () =>
      api.del<ShopifyCredentialsView>(
        `/storefronts/${encodeURIComponent(storefrontId)}/shopify-oauth-credentials`,
      ),
    onSuccess: () => invalidateShopifyCredentials(queryClient, storefrontId),
  });
}

/*
 * The audit log (PRO-1034). The only real control on a surface with no RBAC —
 * every staff member can do everything, and this is how anyone finds out who
 * did.
 */

/** One row of GET /admin/audit-log. See admin.AuditLogListItem. */
export interface AuditLogEntry {
  id: string;
  /** Absent for a system-attributed row, if one is ever written. Every row
   *  written by a handler today carries one. */
  actor_id?: string;
  actor_email: string;
  action: string;
  target_type: string;
  target_id: string;
  /** Already redacted server-side to a per-target-type allowlist. Render
   *  defensively anyway — see AuditLogDiff. */
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  metadata?: { ip?: string; user_agent?: string; request_id?: string };
  created_at: string;
}

export type AuditLogFilters = {
  actor_id?: string;
  action?: string;
  target_type?: string;
  target_id?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
};

/** GET /admin/audit-log. Also how the storefront Activity section reads its
 *  history, with target_type and target_id fixed. */
export function useAuditLog(filters: AuditLogFilters) {
  const api = useApi();
  return useQuery({
    queryKey: keys.auditLog.list(filters),
    queryFn: () => api.get<ListPayload<AuditLogEntry>>(`/audit-log${queryString(filters)}`),
    placeholderData: keepPreviousData,
    retry: retryTransportOnly,
  });
}

/* ------------------------------------------------------------------------- */
/* Payment conflicts (PRO-1345)                                              */
/* ------------------------------------------------------------------------- */

/** One row of GET /admin/payment-conflicts. See admin.PaymentConflictItem. */
export interface PaymentConflict {
  id: string;
  order_id: string;
  /** Today only "paid_after_expired": DurianPay reported a payment complete
   *  for a transaction that had already expired. */
  kind: string;
  psp_payment_id: string;
  /** What DurianPay said was paid, in minor units (sen). */
  amount_minor?: number;
  detail: Record<string, unknown>;
  created_at: string;
  resolved_at?: string;
  resolved_by?: string;
  resolution?: "accepted" | "refunded";
  /** The transaction's status now, not when the conflict was recorded. */
  current_status?: string;
  /** The order's amount, in minor units. */
  expected_minor?: number;
  storefront_id?: string;
  /** Accept would run: open, paid_after_expired, transaction still EXPIRED.
   *  Computed server-side so the button and the guarded write agree. */
  acceptable: boolean;
}

export interface PaymentConflictFilters extends Record<string, unknown> {
  state?: "open" | "resolved" | "all";
  order_id?: string;
  limit?: number;
  offset?: number;
}

/** GET /admin/payment-conflicts. */
export function usePaymentConflicts(filters: PaymentConflictFilters) {
  const api = useApi();
  return useQuery({
    queryKey: keys.paymentConflicts.list(filters),
    queryFn: () =>
      api.get<ListPayload<PaymentConflict>>(`/payment-conflicts${queryString(filters)}`),
    placeholderData: keepPreviousData,
    retry: retryTransportOnly,
  });
}

/**
 * POST /admin/payment-conflicts/:id/accept or /mark-refunded.
 *
 * Accept moves the transaction EXPIRED -> SUCCESSFULL and notifies the
 * merchant, so the transaction, its timeline and the callback queue are
 * invalidated along with the list. silenceErrorToast: the realistic error is
 * a 409 (already resolved, or another payment on the order was accepted),
 * answered in the dialog.
 */
export function useResolvePaymentConflict(conflict: Pick<PaymentConflict, "id" | "order_id">) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    meta: { silenceErrorToast: true },
    mutationFn: ({ action, note }: { action: "accept" | "mark-refunded"; note: string }) =>
      api.post<PaymentConflict>(
        `/payment-conflicts/${encodeURIComponent(conflict.id)}/${action}`,
        note ? { note } : undefined,
      ),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: keys.paymentConflicts.all });
      void queryClient.invalidateQueries({ queryKey: keys.transactions.all });
      void queryClient.invalidateQueries({ queryKey: keys.transactions.detail(conflict.order_id) });
      void queryClient.invalidateQueries({ queryKey: keys.timeline.detail(conflict.order_id) });
      void queryClient.invalidateQueries({ queryKey: keys.callbacks.all });
      void queryClient.invalidateQueries({ queryKey: keys.auditLog.all });
    },
  });
}
